import React,{useState} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiPlay,FiPause,FiMaximize2,FiMonitor,FiSettings,FiEye}=FiIcons;

function App() {
  const [isPlaying,setIsPlaying]=useState(true);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <motion.header
        className="relative overflow-hidden"
        initial={{opacity: 0,y: -50}}
        animate={{opacity: 1,y: 0}}
        transition={{duration: 0.8}}
      >
        <div className="absolute inset-0">
          <img
            src="https://external-preview.redd.it/technical-analysis-of-bambulabs-x1c-network-traffic-v0-hpShYazdF155alXidQqZtX02eRbHVlgVsuimRT-cUN4.jpg?width=1080&crop=smart&auto=webp&s=d13c85cffaf7d7553aecb48d856526577c09c9ab"
            alt="3D Printer Background"
            className="w-full h-full object-cover"
          />
          <img
            src="https://wallpapers.com/images/hd/material-design-1920-x-1280-background-g0axre4rs92rmki5.jpg"
            alt="Material Design Background"
            className="w-full h-full object-cover absolute inset-0"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-8">
          <div className="text-center">
            <motion.div
              className="inline-flex items-center justify-center mb-4"
              initial={{scale: 0.8,opacity: 0}}
              animate={{scale: 1,opacity: 1}}
              transition={{delay: 0.2,duration: 0.6}}
            >
              <img
                src="http://savvymatthew.com/wp-content/uploads/2017/08/SM-Logo-dark.png"
                alt="SavvyMatthew Logo"
                className="h-16 md:h-24 w-auto"
              />
            </motion.div>

            <motion.h2
              className="text-xl md:text-2xl text-gray-300 mb-6"
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              transition={{delay: 0.4,duration: 0.6}}
            >
              Live 3D Printer Camera
            </motion.h2>

            <motion.p
              style={{color: 'rgb(156,163,175)'}}
              className="text-gray-400 max-w-2xl mx-auto text-lg"
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              transition={{delay: 0.6,duration: 0.6}}
            >
              Watch real-time 3D printing in action. Monitor progress,see layer-by-layer creation,and witness the magic of additive manufacturing unfold before your eyes.
            </motion.p>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 pb-12">
        {/* Live Stream Section */}
        <motion.section
          className="mb-12"
          initial={{opacity: 0,y: 50}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 0.8,duration: 0.8}}
        >
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-white font-medium">LIVE</span>
                </div>
                <SafeIcon icon={FiEye} className="w-5 h-5 text-gray-400" />
                <span className="text-gray-400">Watching 3D Print in Progress</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={()=> setIsPlaying(!isPlaying)}
                  className="p-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                >
                  <SafeIcon
                    icon={isPlaying ? FiPause : FiPlay}
                    className="w-4 h-4 text-white"
                  />
                </button>
                <button className="p-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors">
                  <SafeIcon icon={FiMaximize2} className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            <div className="relative rounded-xl overflow-hidden bg-black/20">
              <div className="aspect-video w-full">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://broadcast.nuitv.net:/boroughtv/play.html?id=3dcam"
                  frameBorder="0"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>
          </div>
        </motion.section>

        {/* Laser Engraver Section */}
        <motion.section
          className="mb-12"
          initial={{opacity: 0,y: 50}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 0.9,duration: 0.8}}
        >
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <h2 className="text-xl font-semibold text-white">Live Laser Engraver</h2>
            </div>

            <div className="relative rounded-xl overflow-hidden bg-black/20">
              <div className="aspect-video w-full">
                <iframe
                  width="560"
                  height="315"
                  src="https://broadcast.nuitv.net:/boroughtv/play.html?id=LaserEngraver"
                  frameBorder="0"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>
          </div>
        </motion.section>

        {/* Features Grid */}
        <motion.section
          className="grid md:grid-cols-3 gap-6 mb-12"
          initial={{opacity: 0,y: 50}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 1,duration: 0.8}}
        >
          <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10">
            <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg w-fit mb-4">
              <SafeIcon icon={FiMonitor} className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Real-Time Monitoring</h3>
            <p className="text-gray-400">
              Watch every layer being printed in real-time with our high-quality camera feed.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg w-fit mb-4">
              <SafeIcon icon={FiSettings} className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Professional Setup</h3>
            <p className="text-gray-400">
              Advanced 3D printing equipment with optimal lighting and camera positioning.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10">
            <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg w-fit mb-4">
              <SafeIcon icon={FiEye} className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">24/7 Access</h3>
            <p className="text-gray-400">
              Check in anytime to see what's being created in the SavvyMatthew workshop.
            </p>
          </div>
        </motion.section>

        {/* Status Panel */}
        <motion.section
          className="bg-gradient-to-r from-purple-600/10 to-blue-600/10 backdrop-blur-lg rounded-2xl p-8 border border-white/10"
          initial={{opacity: 0,y: 50}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 1.2,duration: 0.8}}
        >
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Current Print Status</h2>

            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white/5 rounded-lg p-4">
                <div className="text-2xl font-bold text-green-400">ACTIVE</div>
                <div className="text-gray-400 text-sm">Printer Status</div>
              </div>

              <div className="bg-white/5 rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-400">LIVE</div>
                <div className="text-gray-400 text-sm">Camera Feed</div>
              </div>

              <div className="bg-white/5 rounded-lg p-4">
                <div className="text-2xl font-bold text-purple-400">HD</div>
                <div className="text-gray-400 text-sm">Video Quality</div>
              </div>

              <div className="bg-white/5 rounded-lg p-4">
                <div className="text-2xl font-bold text-yellow-400">24/7</div>
                <div className="text-gray-400 text-sm">Availability</div>
              </div>
            </div>
          </div>
        </motion.section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-black/20 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center">
            <p className="text-gray-400 mb-2">
              © 2024 SavvyMatthew. Bringing 3D printing to life through live streaming.
            </p>
            <p className="text-sm text-gray-500">
              Experience the future of maker content with real-time 3D printing monitoring.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;